import { Reveal, MaskedLine } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { IMAGES, CONTACT } from "../data/products";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Leaf, ShieldCheck, Truck } from "lucide-react";

const CHAPTERS = [
  {
    no: "01",
    icon: Leaf,
    title: "Raw, Then Refined",
    body: "It starts at the farm gate. Vegetables, herbs and cane are sourced at peak season, dehydrated gently, and milled fine — so the colour, aroma and nutrition of the original ingredient survives the journey into powder.",
  },
  {
    no: "02",
    icon: ShieldCheck,
    title: "Quality Is A System",
    body: "Every lot passes through mesh-size grading, moisture analysis and colour-strength checks before it earns the MSD name. Food safety isn't a department here — it's the process itself.",
  },
  {
    no: "03",
    icon: Truck,
    title: "From Vatva To The World",
    body: "As manufacturers and exporters, we handle bulk orders, private-label packing and export documentation end to end. Your production line never waits on us.",
  },
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-[72px]">
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pt-20 sm:pt-32 pb-24">
        <p className="overline-label mb-8">About MSD Foods</p>
        <h1 className="font-display font-black tracking-tighter leading-[0.9] text-[clamp(3rem,9vw,8rem)]" data-testid="about-heading">
          <MaskedLine delay={0.1}>A HOUSE OF</MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="text-[#D97736]">FOOD POWDERS.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-12 max-w-2xl text-base sm:text-lg leading-relaxed text-[#f5f5f5]/70" data-testid="about-intro">
            MSD Foods is a manufacturer and exporter of caramel colours, food flavour
            powders and industrial food ingredients, operating out of Ahmedabad,
            India. We serve food processors, beverage brands, bakeries and seasoning
            houses that need one thing above all: a supplier who never breaks spec.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {[
            { src: IMAGES.turmeric, caption: "Golden lots, QC sampling", testid: "about-img-turmeric" },
            { src: IMAGES.spoonsPowder, caption: "Flavour powders, graded & milled", testid: "about-img-spoons" },
          ].map((img, i) => (
            <Reveal key={img.testid} delay={0.1 + i * 0.12}>
              <figure className="group" data-testid={img.testid}>
                <div className="relative overflow-hidden clip-frame h-[320px] sm:h-[420px]">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/20 transition-colors duration-500 group-hover:bg-black/0" />
                </div>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="w-6 h-px bg-[#D97736]" />
                  <span className="font-mono-jb text-[10px] uppercase tracking-[0.25em] text-[#f5f5f5]/50">
                    {img.caption}
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pb-24 sm:pb-32">
        <Reveal>
          <div className="relative overflow-hidden clip-frame h-[420px] sm:h-[560px]" data-testid="about-plant-image">
            <img src={IMAGES.plant} alt="MSD Foods production facility" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/35" />
            <div className="absolute bottom-0 left-0 p-8 sm:p-12">
              <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-[#D97736] mb-3">G.I.D.C. Vatva, Ahmedabad</p>
              <p className="font-display text-2xl sm:text-4xl font-bold tracking-tight max-w-xl">
                One roof. One industrial estate. Total control.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <EditorialMarquee />

      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 py-24 sm:py-36">
        <Reveal>
          <p className="overline-label mb-4">How We Work</p>
          <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tighter leading-none mb-20">
            The process,<br />chapter by chapter.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-white/10">
          {CHAPTERS.map((ch, i) => (
            <Reveal key={ch.no} delay={i * 0.12} className="border-r border-b border-white/10">
              <div className="group p-10 sm:p-12 h-full transition-colors duration-500 hover:bg-[#111111]" data-testid={`about-chapter-${ch.no}`}>
                <div className="flex items-start justify-between mb-16">
                  <span className="font-display text-6xl sm:text-7xl font-black tracking-tighter text-outline transition-all duration-500 group-hover:text-[#D97736] group-hover:[-webkit-text-stroke:0px]">
                    {ch.no}
                  </span>
                  <ch.icon className="w-7 h-7 text-[#D97736]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl font-bold tracking-tight mb-4">{ch.title}</h3>
                <p className="text-sm leading-relaxed text-[#f5f5f5]/60">{ch.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pb-24 sm:pb-36">
        <Reveal>
          <p className="overline-label mb-4">Find Us</p>
          <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tighter leading-none mb-16">
            Head office.
          </h2>
        </Reveal>
        <div className="border border-white/10">
          <Reveal>
            <div
              className="group flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-10 px-8 sm:px-12 py-8 transition-colors duration-500 hover:bg-[#111111]"
              data-testid="head-office-row"
            >
              <div className="flex items-center gap-5 sm:w-56 shrink-0">
                <Building2 className="w-6 h-6 text-[#D97736]" strokeWidth={1.5} />
                <span className="font-display text-2xl font-bold tracking-tight">Head Office</span>
              </div>
              <p className="text-sm text-[#f5f5f5]/60 leading-relaxed">{CONTACT.headOffice}</p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="group inline-flex items-center gap-3 mt-14 bg-[#D97736] text-[#0a0a0a] font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-[#e8904f]"
          >
            Start a conversation
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
